import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import apiClient from "utils/apiClient";
import { PM_SERVICE_BASE_URL } from "constants";

const ProjectsContext = createContext();

export const useProjectsContext = () => {
  const context = useContext(ProjectsContext);
  if (!context) {
    throw new Error(
      "useProjectsContext must be used within a ProjectsProvider",
    );
  }
  return context;
};

const ProjectsProvider = ({ children }) => {
  const [state, setState] = useState({
    projects: [],
    loading: false,
    error: null,
  });

  /** Generic GET helper **/
  const getRequest = useCallback(async (url) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.get(url, {
        params: { sort: "createdAt,desc" },
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      console.error("[ProjectsProvider:getRequest]", errorMessage);
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic POST helper **/
  const postRequest = useCallback(async (url, payload) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.post(url, payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      console.error("[ProjectsProvider:postRequest]", errorMessage);
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic PUT helper **/
  const putRequest = useCallback(async (url, payload) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.put(url, payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      console.error("[ProjectsProvider:putRequest]", errorMessage);
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic DELETE helper **/
  const deleteRequest = useCallback(async (url) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await apiClient.delete(url);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      console.error("[ProjectsProvider:deleteRequest]", errorMessage);
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Fetch all projects **/
  const fetchProjects = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects`;
      const response = await apiClient.get(url, {
        params: {
          sort: "createdAt,desc",
          page: 0,
        },
      });
      const data = response.data;

      // console.log("[ProjectsProvider] API Response:", data);

      // Handle paginated response (content array) or direct array/object
      let projectsArray = [];
      if (data && data.content && Array.isArray(data.content)) {
        // Paginated response
        projectsArray = data.content;
      } else if (Array.isArray(data)) {
        // Direct array response
        projectsArray = data;
      } else if (data && typeof data === "object") {
        // Single object response
        projectsArray = [data];
      }

      // console.log("[ProjectsProvider] Processed projects array:", projectsArray);

      setState((prev) => ({
        ...prev,
        projects: projectsArray,
        loading: false,
      }));
    } catch (err) {
      console.error("[ProjectsProvider:fetchProjects]", err);
      const errorMessage = err.response?.data?.message || err.message;
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage || "Failed to fetch projects",
      }));
    }
  }, []);

  /** Fetch single project by ID **/
  const getProjectById = useCallback(
    async (projectId) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}`;
        const data = await getRequest(url);
        return data;
      } catch (err) {
        console.error("[ProjectsProvider:getProjectById]", err);
        throw err;
      }
    },
    [getRequest],
  );

  /** Create project **/
  const createProject = useCallback(
    async (projectData) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects`;
        const data = await postRequest(url, projectData);

        // Add to projects list without full refetch (newest first)
        setState((prev) => ({
          ...prev,
          projects: [
            data,
            ...(prev.projects || []).filter(
              (project) => project.id !== data.id,
            ),
          ],
          loading: false,
        }));

        return data;
      } catch (err) {
        console.error("[ProjectsProvider:createProject]", err);
        throw err;
      }
    },
    [postRequest],
  );

  /** Update project **/
  const updateProject = useCallback(
    async (projectId, projectData) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}`;
        const data = await putRequest(url, projectData);

        // Refresh projects list
        // await fetchProjects();

        return data;
      } catch (err) {
        console.error("[ProjectsProvider:updateProject]", err);
        throw err;
      }
    },
    [putRequest, fetchProjects],
  );

  /** Delete project **/
  const deleteProject = useCallback(
    async (projectId) => {
      try {
        const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}`;
        await deleteRequest(url);

        // Refresh projects list
        await fetchProjects();

        return { success: true };
      } catch (err) {
        console.error("[ProjectsProvider:deleteProject]", err);

        // Handle foreign key constraint errors specifically
        if (
          err.response?.data?.message?.includes(
            "violates foreign key constraint",
          ) ||
          err.response?.data?.message?.includes("still referenced")
        ) {
          throw new Error(
            "Cannot delete project because it still contains iterations or tasks. Please delete all iterations first.",
          );
        }

        // Handle other specific error cases
        if (err.response?.status === 400) {
          throw new Error("Invalid request. Please check project details.");
        }

        if (err.response?.status === 404) {
          throw new Error(
            "Project not found. It may have already been deleted.",
          );
        }

        if (err.response?.status === 500) {
          throw new Error("Server error. Please try again later.");
        }

        throw err;
      }
    },
    [deleteRequest, fetchProjects],
  );

  // Auto-fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const value = {
    projects: state.projects,
    loading: state.loading,
    error: state.error,
    fetchProjects,
    getProjectById,
    createProject,
    updateProject,
    deleteProject,
  };

  return (
    <ProjectsContext.Provider value={value}>
      {children}
    </ProjectsContext.Provider>
  );
};

ProjectsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ProjectsProvider;
